/**
 * Football, v5 rules (app/LagKing/FOOTBALL_DESIGN_PROPOSAL.md, "v5 (chosen
 * 2026-10-17)"; the balance numbers come from scripts/football-sim.mjs
 * `--rules v5`).
 *
 * Cole, 2026-10-17: "I think A works. Yards are where your ball stops.
 * 3->1.5->Make->Gone just like baseball so you gotta mix and match. maybe have
 * 5 different types. If the defense gets inside your putt they break it up for
 * no gain. a missed ring putt, the opponent can hole it for a turnover (either
 * fumble for first 2 or INT for last 3)."
 *
 * A pure reducer, like Baseball: state = fold(putts and choices), so local
 * play, the computer and live online share it. The engine never sees a raw
 * putt; the caller reads it against every cup first (footballRead.ts) so the
 * make model stays in one place. Nothing is random.
 *
 * Teams: 0 = away (the invited player, ball first), 1 = home. Yard lines are
 * measured from the offense's own goal line: 65 is the opponent's 35.
 */

import { ADULT_DISTANCES, type DistanceView } from './kidDistances';

/** The five plays on the line, nearest first: two runs, three passes. */
export const FOOTBALL_CUPS = ['inside', 'outside', 'short', 'medium', 'deep'] as const;
export type FootballCup = (typeof FOOTBALL_CUPS)[number];

export type FootballCupKind = 'run' | 'pass';

export interface FootballCupSpec {
  label: string;
  kind: FootballCupKind;
  /** Distance down the putt line from the ball. */
  distanceFt: number;
}

/** The cups (scripts/football-sim.mjs PLAYS_V5; the parity test checks it). */
export const FOOTBALL_CUP_SPECS: Record<FootballCup, FootballCupSpec> = {
  inside: { label: 'INSIDE RUN', kind: 'run', distanceFt: 9 },
  outside: { label: 'OUTSIDE RUN', kind: 'run', distanceFt: 18 },
  short: { label: 'SHORT PASS', kind: 'pass', distanceFt: 27 },
  medium: { label: 'MEDIUM PASS', kind: 'pass', distanceFt: 36 },
  deep: { label: 'DEEP PASS', kind: 'pass', distanceFt: 45 },
};

/** The field is real scale: three feet of roll is one yard. */
export const FOOTBALL_FT_PER_YARD = 3;

/**
 * Baseball's progressive tolerance, per play, per team, for the whole game:
 * 3 ft, then 1.5 ft, then hole it, then gone (stage 3). A play steps up when
 * its gain stands; once a team has gained with all five, all five reset.
 */
export const FOOTBALL_STAGE_RING_FT = [3, 1.5, 0] as const;
export type FootballStage = 0 | 1 | 2 | 3;
export type FootballStages = Record<FootballCup, FootballStage>;
export type FootballEarned = Record<FootballCup, boolean>;

/** Every drive starts at the opponent's 35 (Cole, 2026-10-17); overtime at the opponent's 25. */
export const FOOTBALL_START_YL = 65;
export const FOOTBALL_OVERTIME_YL = 75;
export const FOOTBALL_PUNT_NET_YARDS = 40;
export const FOOTBALL_PUNT_TOUCHBACK_YL = 20;
/** Field goal: a cup at the kick distance less 14 (footballKickFt), good holed or within 3 ft, 55 yards at most. */
export const FOOTBALL_FG_RING_FT = 3;
export const FOOTBALL_FG_MAX_YARDS = 55;
export const FOOTBALL_TWO_POINT_YL = 98;
/**
 * Possessions, not a snap clock (Cole, 2026-10-17: "if you only have a few
 * snaps left in the half or in the game, you just cannot get to the end zone.
 * I think we have to do possession based instead of snap based"). Drives a
 * half, alternating, the half's receiver first; every drive plays to its end.
 * Away receives the first half and home the second, so an odd count still
 * gives both sides the same number a game: Standard 3 drives a half (3 each a
 * game), Quick 2 (2 each). Chosen with scripts/football-sim.mjs --v5poss so a
 * game stays about as long as the 14 and 10 snap clocks were.
 */
export const FOOTBALL_HALF_DRIVES = { standard: 3, quick: 2 } as const;
export type FootballLength = keyof typeof FOOTBALL_HALF_DRIVES;

export type FootballTeam = 0 | 1;
/** `block`: a field goal finished in its ring and the defense putts at the kick cup; holing it blocks the kick (Cole, 2026-10-18). */
export type FootballPhase = 'offense' | 'defense' | 'fourthDown' | 'kick' | 'block' | 'tryChoice' | 'complete';
export type FootballFourthDownCall = 'go' | 'kick' | 'punt';
export type FootballTryCall = 'take7' | 'goForTwo';

export interface FootballCupRead {
  leaveFt: number;
  holed: boolean;
}

/** One putt read against every cup, the ball's rest down the line, and the kick cup when one was asked for (footballRead.ts). */
export interface FootballRead {
  cups: Record<FootballCup, FootballCupRead>;
  /** How far down the line the ball stopped (ft), as the scene places it. */
  restFt: number;
  kick: (FootballCupRead & { targetFt: number }) | null;
  /** The ball struck the gate: the putt does not count (Cole, 2026-10-16). */
  gateHit: boolean;
}

export type FootballResult =
  | 'gain' | 'holedRun' | 'noGain' | 'fumble'
  | 'complete' | 'completeHoled' | 'incomplete' | 'intercepted'
  | 'brokenUp';

/**
 * What the defense is answering. A play that is on (in its ring): finish
 * inside the offense's ball, or hole it, and it is broken up. A missed ring:
 * hole the cup the ball stopped nearest for the turnover.
 */
export type FootballPending =
  | { kind: 'live'; cup: FootballCup; offenseLeaveFt: number; gainYards: number }
  | { kind: 'loose'; cup: FootballCup; offenseLeaveFt: number };

export interface FootballAnswer {
  leaveFt: number;
  holed: boolean;
  /** The answer changed the offense's result. */
  changed: boolean;
}

/** One finished snap, for the HUD and the TV. */
export interface FootballSnap {
  team: FootballTeam;
  /** The cup the ball ended at. */
  cup: FootballCup;
  result: FootballResult;
  /** Yards applied (a touchdown is the distance to the goal line). */
  yards: number;
  fromYl: number;
  toYl: number;
  down: 1 | 2 | 3 | 4;
  togo: number;
  offenseLeaveFt: number | null;
  offenseHoled: boolean;
  answer: FootballAnswer | null;
  /** The cup's stage when the ball was read. */
  stage: FootballStage;
  firstDown: boolean;
  touchdown: boolean;
  turnover: boolean;
  twoPoint: boolean;
}

export type FootballDriveResult =
  | 'touchdown' | 'fieldGoal' | 'missedFieldGoal' | 'blockedFieldGoal' | 'punt' | 'interception' | 'fumble' | 'downs' | 'safety' | 'clock';

export interface FootballDrive {
  team: FootballTeam;
  /** 1 or 2 in regulation, 3 and up in overtime (3 = overtime round 1). */
  half: number;
  startYl: number;
  snaps: number;
  yards: number;
  result: FootballDriveResult;
  points: number;
}

export interface FootballState {
  /** Drives a half, both sides together (3 standard, 2 quick): each side gets this many a game. */
  halfDrives: number;
  /** 1, 2, then overtime. */
  half: 1 | 2;
  /** Drives left in the half, the current one included; overtime has its own count. */
  drivesLeft: number;
  /** 0 in regulation, then the overtime round. */
  overtime: number;
  offense: FootballTeam;
  phase: FootballPhase;
  down: 1 | 2 | 3 | 4;
  togo: number;
  yl: number;
  /** Each team's ring stages, for the whole game. */
  stages: [FootballStages, FootballStages];
  /** Which plays each team has gained with since its last reset. */
  earned: [FootballEarned, FootballEarned];
  pending: FootballPending | null;
  /** The snap is a two-point try (one snap from the 2). */
  twoPoint: boolean;
  scores: [number, number];
  drive: { startYl: number; snaps: number; yards: number; offenseScoreAtStart: number };
  drives: FootballDrive[];
  lastSnap: FootballSnap | null;
  winner: FootballTeam | null;
  /** Box-score counts by team. */
  touchdowns: [number, number];
  fieldGoals: [number, number];
  interceptionsThrown: [number, number];
  fumblesLost: [number, number];
  longestPlay: [number, number];
  /** Finished snaps (two-point tries included). */
  snaps: number;
}

export type FootballEvent =
  | { type: 'driveStart'; team: FootballTeam; yl: number; half: number }
  | { type: 'pending'; pending: FootballPending }
  | { type: 'snap'; snap: FootballSnap }
  | { type: 'seen'; team: FootballTeam; cup: FootballCup; stage: FootballStage }
  | { type: 'reset'; team: FootballTeam }
  | { type: 'firstDown'; team: FootballTeam; yl: number }
  | { type: 'touchdown'; team: FootballTeam }
  | { type: 'try'; team: FootballTeam; points: 0 | 1 | 2; twoPoint: boolean }
  | { type: 'tryChoice'; team: FootballTeam }
  | { type: 'fourthDown'; team: FootballTeam }
  | { type: 'fieldGoal'; team: FootballTeam; good: boolean; kickYards: number; blocked?: boolean }
  /** A kick in its ring: the defense putts at the kick cup to block it. */
  | { type: 'blockChance'; team: FootballTeam; kickYards: number }
  | { type: 'punt'; team: FootballTeam; receivingYl: number }
  | { type: 'safety'; team: FootballTeam }
  | { type: 'turnover'; team: FootballTeam; how: 'interception' | 'fumble' | 'downs' }
  | { type: 'halftime' }
  | { type: 'overtime'; round: number }
  | { type: 'gameOver'; winner: FootballTeam; clinched?: boolean };

export interface AppliedFootball {
  state: FootballState;
  events: FootballEvent[];
}

const freshStages = (): FootballStages => ({ inside: 0, outside: 0, short: 0, medium: 0, deep: 0 });
const freshEarned = (): FootballEarned => ({ inside: false, outside: false, short: false, medium: false, deep: false });

export function createFootballGame(options: { length?: FootballLength; halfDrives?: number } = {}): FootballState {
  const halfDrives = Math.max(1, Math.round(options.halfDrives ?? FOOTBALL_HALF_DRIVES[options.length ?? 'standard']));
  return {
    halfDrives,
    half: 1,
    drivesLeft: halfDrives,
    overtime: 0,
    offense: 0,
    phase: 'offense',
    down: 1,
    togo: 10,
    yl: FOOTBALL_START_YL,
    stages: [freshStages(), freshStages()],
    earned: [freshEarned(), freshEarned()],
    pending: null,
    twoPoint: false,
    scores: [0, 0],
    drive: { startYl: FOOTBALL_START_YL, snaps: 0, yards: 0, offenseScoreAtStart: 0 },
    drives: [],
    lastSnap: null,
    winner: null,
    touchdowns: [0, 0],
    fieldGoals: [0, 0],
    interceptionsThrown: [0, 0],
    fumblesLost: [0, 0],
    longestPlay: [0, 0],
    snaps: 0,
  };
}

function clone(state: FootballState): FootballState {
  return {
    ...state,
    stages: [{ ...state.stages[0] }, { ...state.stages[1] }],
    earned: [{ ...state.earned[0] }, { ...state.earned[1] }],
    scores: [...state.scores] as [number, number],
    drive: { ...state.drive },
    drives: [...state.drives],
    touchdowns: [...state.touchdowns] as [number, number],
    fieldGoals: [...state.fieldGoals] as [number, number],
    interceptionsThrown: [...state.interceptionsThrown] as [number, number],
    fumblesLost: [...state.fumblesLost] as [number, number],
    longestPlay: [...state.longestPlay] as [number, number],
  };
}

// ---------------------------------------------------------------------------
// Queries for the UI
// ---------------------------------------------------------------------------

export function footballDefense(state: Pick<FootballState, 'offense'>): FootballTeam {
  return (1 - state.offense) as FootballTeam;
}

/** Whose putt is next; null when nobody putts (a choice is pending, or the game is over). */
export function footballPutter(state: FootballState): FootballTeam | null {
  if (state.phase === 'offense' || state.phase === 'kick') return state.offense;
  if (state.phase === 'defense' || state.phase === 'block') return footballDefense(state);
  return null;
}

/** Who makes the pending choice (4th down or the last snap, the two-point try); null otherwise. */
export function footballChooser(state: FootballState): FootballTeam | null {
  return state.phase === 'fourthDown' || state.phase === 'tryChoice' ? state.offense : null;
}

export function footballGoalYards(state: Pick<FootballState, 'yl'>): number {
  return 100 - state.yl;
}

/** A cup's stage for the side with the ball. */
export function footballCupStage(state: Pick<FootballState, 'stages' | 'offense'>, cup: FootballCup): FootballStage {
  return state.stages[state.offense][cup];
}

/** The end line is 10 yards past the goal line. */
export const FOOTBALL_END_LINE_YL = 110;

/**
 * A cup is a play only when it sits on the field or in the end zone, short
 * of the end line (Cole, 2026-10-17: "being able to go for putts out of the
 * end zone"). From the 2, the 36 and 45 ft cups are past it. Without a spot
 * (an old caller) every cup counts.
 */
export function footballCupInBounds(state: { yl?: number }, cup: FootballCup): boolean {
  if (typeof state.yl !== 'number') return true;
  return FOOTBALL_CUP_SPECS[cup].distanceFt < (FOOTBALL_END_LINE_YL - state.yl) * FOOTBALL_FT_PER_YARD;
}

/** The cups on the green for the side with the ball, nearest first: every play not gone, short of the end line. */
export function footballCupsOnGreen(state: Pick<FootballState, 'stages' | 'offense'> & { yl?: number }): FootballCup[] {
  const live = FOOTBALL_CUPS.filter((cup) => footballCupStage(state, cup) < 3 && footballCupInBounds(state, cup));
  // Every in-bounds play gone at once (deep in the red zone): they still
  // read the ball, a missed ring is a loose ball.
  return live.length > 0 ? live : FOOTBALL_CUPS.filter((cup) => footballCupInBounds(state, cup));
}

/** The ring at a stage: 3, 1.5, 0 (hole it); null once gone. */
export function footballRingFt(stage: FootballStage): number | null {
  return stage === 3 ? null : FOOTBALL_STAGE_RING_FT[stage];
}

/** "3 FT", "1.5 FT", "HOLE IT", "GONE": Baseball's words. Rookie's rings are a third, Pro's half (kidDistances.ts). */
export function footballNeedLabel(stage: FootballStage): string {
  return footballNeedLabelFor(stage, ADULT_DISTANCES);
}
export function footballNeedLabelFor(stage: FootballStage, view: DistanceView): string {
  if (stage === 3) return 'GONE';
  if (stage === 2) return 'HOLE IT';
  return `${view.tolFt(FOOTBALL_STAGE_RING_FT[stage])} FT`;
}

/** Yards for a ball that stopped `restFt` down the line (3 ft = 1 yd, rounded). */
export function footballYardsForRest(restFt: number): number {
  return Math.round(Math.max(0, restFt) / FOOTBALL_FT_PER_YARD);
}

/** A holed ball doubles the gain at its cup: 6, 12, 18, 24, 30 yards. */
export const FOOTBALL_HOLED_MULTIPLIER = 2;
export function footballHoledYards(cup: FootballCup): number {
  return FOOTBALL_HOLED_MULTIPLIER * footballYardsForRest(FOOTBALL_CUP_SPECS[cup].distanceFt);
}

export interface FootballCupView {
  cup: FootballCup;
  label: string;
  kind: FootballCupKind;
  distanceFt: number;
  stage: FootballStage;
  /** The ring at its current stage; 0 = hole it; null = gone. */
  ringFt: number | null;
  needLabel: string;
  /** Short of the end line: a play from this spot (footballCupInBounds). */
  inBounds: boolean;
}

/** Every play for the side with the ball, gone ones included (the table lists them). */
export function footballCupViews(state: Pick<FootballState, 'stages' | 'offense'> & { yl?: number }, view: DistanceView = ADULT_DISTANCES): FootballCupView[] {
  return FOOTBALL_CUPS.map((cup) => {
    const spec = FOOTBALL_CUP_SPECS[cup];
    const stage = footballCupStage(state, cup);
    return { cup, label: spec.label, kind: spec.kind, distanceFt: spec.distanceFt, stage, ringFt: footballRingFt(stage), needLabel: footballNeedLabelFor(stage, view), inBounds: footballCupInBounds(state, cup) };
  });
}

/** The field-goal distance in yards from the current spot. */
export function footballKickYards(state: Pick<FootballState, 'yl'>): number {
  return 100 - state.yl + 17;
}

export function footballFieldGoalInRange(state: Pick<FootballState, 'yl'>): boolean {
  return footballKickYards(state) <= FOOTBALL_FG_MAX_YARDS;
}

/**
 * The kick cup, to the half foot: a chip shot is a short, easy putt and a
 * long kick a long one (Cole, 2026-10-17: "a field goal from the 2 yard line
 * being harder than a short run for a touchdown"). 18 yards (the 1) is a
 * 4 ft putt, 19 (the 2) 5 ft, 35 yards 21 ft, 55 yards 41 ft: one foot of putt
 * per yard past 14. The ring stays 3 ft, so inside the 10 the kick is far
 * easier than the 9 ft run with a defense to beat.
 */
export const FOOTBALL_KICK_FT_OFFSET_YARDS = 14;
export function footballKickFt(state: Pick<FootballState, 'yl'>): number {
  return Math.max(4, footballKickYards(state) - FOOTBALL_KICK_FT_OFFSET_YARDS);
}

/** No punting at or inside the opponent's 40 (Cole, 2026-10-17: "being able to punt from the 2 yard line"). */
export const FOOTBALL_NO_PUNT_YL = 60;
export function footballPuntAllowed(state: Pick<FootballState, 'yl'>): boolean {
  return state.yl < FOOTBALL_NO_PUNT_YL;
}

/**
 * Behind on the side's last drive of regulation (Cole, 2026-10-29: "needs to
 * be a guaranteed go for it when it's 4th down and you're on very last
 * posession and are losing"): a punt gives the game away, and a field goal
 * helps only when 3 points ties or wins. Null when the normal calls apply.
 */
export function footballLastDriveCalls(state: FootballState): FootballFourthDownCall[] | null {
  if (state.overtime > 0) return null;
  const team = state.offense;
  const behind = state.scores[team === 0 ? 1 : 0] - state.scores[team];
  if (behind <= 0 || footballDrivesRemaining(state)[team] !== 1) return null;
  return behind <= 3 && footballFieldGoalInRange(state) ? ['go', 'kick'] : ['go'];
}

/** The legal calls at the 4th-down card. */
export function footballFourthDownCalls(state: FootballState): FootballFourthDownCall[] {
  const lastDrive = footballLastDriveCalls(state);
  if (lastDrive) return lastDrive;
  const kick = footballFieldGoalInRange(state);
  const calls: FootballFourthDownCall[] = ['go'];
  if (kick) calls.push('kick');
  if (state.down === 4 && footballPuntAllowed(state)) calls.push('punt');
  return calls;
}

/** The distance the gate is aimed at for the next putt: the kick cup, the cup being answered, or null (the whole line). */
export function footballTargetFt(state: FootballState): number | null {
  if (state.phase === 'kick' || state.phase === 'block') return footballKickFt(state);
  if (state.phase === 'defense' && state.pending) return FOOTBALL_CUP_SPECS[state.pending.cup].distanceFt;
  return null;
}

export interface FootballDefenseNeed {
  cup: FootballCup;
  distanceFt: number;
  /** breakUp: finish inside the offense's ball; takeAway: hole it after a missed ring. */
  kind: 'breakUp' | 'takeAway';
  /** The offense's leave (the ring the defense must beat); null on a loose ball. */
  insideFt: number | null;
  label: string;
}

/**
 * What the defense must do with its putt. `view`: the defense's field, so a
 * Rookie or Pro defense reads its level's cup and the offense's leave at its scale; distanceFt and
 * insideFt stay in engine feet.
 */
export function footballDefenseNeed(state: FootballState, view: DistanceView = ADULT_DISTANCES): FootballDefenseNeed | null {
  const pending = state.phase === 'defense' ? state.pending : null;
  if (!pending) return null;
  const spec = FOOTBALL_CUP_SPECS[pending.cup];
  const base = { cup: pending.cup, distanceFt: spec.distanceFt };
  const cupFt = view.targetFt(spec.distanceFt);
  if (pending.kind === 'loose') {
    return { ...base, kind: 'takeAway', insideFt: null, label: `HOLE THE ${cupFt} FT CUP FOR ${spec.kind === 'run' ? 'A FUMBLE' : 'AN INTERCEPTION'}` };
  }
  // The proximity to beat lives in the scoreboard's status line (Cole,
  // 2026-10-17: "to beat 2.0 ft ... should go in the upper right card").
  const shownLeave = view.tolFt(pending.offenseLeaveFt);
  const leave = shownLeave < 1 ? `${Math.max(1, Math.round(shownLeave * 12))} IN` : `${shownLeave.toFixed(1)} FT`;
  return { ...base, kind: 'breakUp', insideFt: pending.offenseLeaveFt, label: `BEAT ${leave} AT THE ${cupFt} FT CUP` };
}

/** "OWN 35", "MIDFIELD", "OPP 38" from the offense's side. */
export function footballSpotLabel(yl: number): string {
  if (yl === 50) return 'MIDFIELD';
  return yl < 50 ? `OWN ${yl}` : `OPP ${100 - yl}`;
}

const DOWN_NAMES = ['', '1ST', '2ND', '3RD', '4TH'] as const;

/** "3RD & 4 . OPP 38", "1ST & GOAL . OPP 5", "2-PT TRY". */
export function footballDownLabel(state: FootballState): string {
  if (state.twoPoint) return '2-PT TRY';
  const distance = state.togo >= 100 - state.yl ? 'GOAL' : String(state.togo);
  return `${DOWN_NAMES[state.down]} & ${distance} . ${footballSpotLabel(state.yl)}`;
}

/** The down and distance alone, big in the corner: "3RD & 4", "1ST & GOAL", "2-PT". */
export function footballDownShort(state: FootballState): string {
  if (state.twoPoint) return '2-PT';
  const distance = state.togo >= 100 - state.yl ? 'GOAL' : String(state.togo);
  return `${DOWN_NAMES[state.down]} & ${distance}`;
}

/** "1ST HALF", "2ND HALF", "OT 1", "FINAL". */
export function footballHalfLabel(state: FootballState): string {
  if (state.phase === 'complete') return 'FINAL';
  if (state.overtime > 0) return `OT ${state.overtime}`;
  return state.half === 1 ? '1ST HALF' : '2ND HALF';
}

/** Each side's drives a game (both halves): the same for both. */
export function footballDrivesEach(state: Pick<FootballState, 'halfDrives'>): number {
  return state.halfDrives;
}

/** The side with the ball's drive number this game (regulation), 1-based. */
export function footballDriveNumber(state: Pick<FootballState, 'offense' | 'drives'>): number {
  return state.drives.filter((drive) => drive.team === state.offense && drive.half <= 2).length + 1;
}

/** "DRIVE 2 OF 3" for the side with the ball; null in overtime and after the game. */
export function footballClockLabel(state: FootballState): string | null {
  if (state.phase === 'complete' || state.overtime > 0) return null;
  return `DRIVE ${Math.min(footballDriveNumber(state), footballDrivesEach(state))} OF ${footballDrivesEach(state)}`;
}

/** The compact form for the phone's scoreboard header: "DRIVE 2/3"; null in overtime and after the game. */
export function footballClockShort(state: FootballState): string | null {
  if (state.phase === 'complete' || state.overtime > 0) return null;
  return `DRIVE ${Math.min(footballDriveNumber(state), footballDrivesEach(state))}/${footballDrivesEach(state)}`;
}

export function footballCupLabel(cup: FootballCup): string {
  return FOOTBALL_CUP_SPECS[cup].label;
}

/** The result word. */
export function footballResultLabel(result: FootballResult): string {
  switch (result) {
    case 'gain': return 'GAIN';
    case 'holedRun': return 'HOLED';
    case 'noGain': return 'NO GAIN';
    case 'fumble': return 'FUMBLE';
    case 'complete': return 'COMPLETE';
    case 'completeHoled': return 'HOLED';
    case 'incomplete': return 'INCOMPLETE';
    case 'intercepted': return 'INTERCEPTED';
    case 'brokenUp': return 'BROKEN UP';
  }
}

/** The go-for-two window: each side's last drive of the game, or overtime. */
export function footballLateGame(state: Pick<FootballState, 'overtime' | 'half' | 'drivesLeft'>): boolean {
  return state.overtime > 0 || (state.half === 2 && state.drivesLeft <= 2);
}

/** No possession follows this one: the last drive of the second half, or home's drive in an overtime round. */
export function footballLastPossession(state: Pick<FootballState, 'overtime' | 'half' | 'drivesLeft' | 'offense'>): boolean {
  return state.overtime > 0 ? state.offense === 1 : state.half === 2 && state.drivesLeft <= 1;
}

// ---------------------------------------------------------------------------
// The snap
// ---------------------------------------------------------------------------

/** Where the ball ended: the nearest live cup holed in, else the nearest by leave (a tie goes to the shorter cup). */
function endedAt(read: FootballRead, cups: readonly FootballCup[]): FootballCup {
  const holed = cups.find((cup) => read.cups[cup].holed);
  if (holed) return holed;
  return cups.reduce((best, cup) => (read.cups[cup].leaveFt < read.cups[best].leaveFt ? cup : best), cups[0]);
}

interface Outcome {
  cup: FootballCup;
  result: FootballResult;
  yards: number;
  turnover: boolean;
  offenseLeaveFt: number | null;
  offenseHoled: boolean;
  answer: FootballAnswer | null;
  stage: FootballStage;
}

function applyOffense(state: FootballState, read: FootballRead, events: FootballEvent[]): void {
  const cups = footballCupsOnGreen(state);
  const cup = endedAt(read, cups);
  const spec = FOOTBALL_CUP_SPECS[cup];
  const stage = footballCupStage(state, cup);
  const { leaveFt, holed } = read.cups[cup];

  // Holed: double the gain at the cup (Cole, 2026-10-17: "I do want holing
  // bonus"), and nothing to answer.
  if (holed) {
    const result: FootballResult = spec.kind === 'pass' ? 'completeHoled' : 'holedRun';
    finishSnap(state, { cup, result, yards: footballHoledYards(cup), turnover: false, offenseLeaveFt: 0, offenseHoled: true, answer: null, stage }, events);
    return;
  }
  const ring = footballRingFt(stage) ?? 0;
  if (ring > 0 && leaveFt <= ring) {
    // On: the yards are where the ball stopped; the defense answers from the same cup.
    state.pending = { kind: 'live', cup, offenseLeaveFt: leaveFt, gainYards: footballYardsForRest(read.restFt) };
  } else if (state.twoPoint) {
    // A missed try is simply no good: nothing for the defense to take away.
    finishSnap(state, { cup, result: spec.kind === 'run' ? 'noGain' : 'incomplete', yards: 0, turnover: false, offenseLeaveFt: leaveFt, offenseHoled: false, answer: null, stage }, events);
    return;
  } else if (state.down === 4) {
    // Going for it on 4th down and missing every ring is a turnover on downs
    // whatever the defense does, so it gets no putt for a takeaway (Cole,
    // 2026-10-19). The spot is the same as an interception's or a fumble's,
    // the line of scrimmage; no interception or fumble is charged.
    finishSnap(state, { cup, result: spec.kind === 'run' ? 'noGain' : 'incomplete', yards: 0, turnover: false, offenseLeaveFt: leaveFt, offenseHoled: false, answer: null, stage }, events);
    return;
  } else {
    // A missed ring: no gain, unless the defense holes that cup (a turnover).
    state.pending = { kind: 'loose', cup, offenseLeaveFt: leaveFt };
  }
  state.phase = 'defense';
  events.push({ type: 'pending', pending: state.pending });
}

function applyDefense(state: FootballState, read: FootballRead, events: FootballEvent[]): void {
  const pending = state.pending!;
  const spec = FOOTBALL_CUP_SPECS[pending.cup];
  const answerRead = read.cups[pending.cup];
  const stage = footballCupStage(state, pending.cup);
  const answer = (changed: boolean): FootballAnswer => ({ leaveFt: answerRead.holed ? 0 : answerRead.leaveFt, holed: answerRead.holed, changed });
  const base = { cup: pending.cup, offenseLeaveFt: pending.offenseLeaveFt, offenseHoled: false, stage };
  state.pending = null;

  if (pending.kind === 'loose') {
    const taken = answerRead.holed;
    const result: FootballResult = taken ? (spec.kind === 'run' ? 'fumble' : 'intercepted') : spec.kind === 'run' ? 'noGain' : 'incomplete';
    finishSnap(state, { ...base, result, yards: 0, turnover: taken, answer: answer(taken) }, events);
    return;
  }
  // Inside the offense's ball (or holed) breaks it up; a tie goes to the offense.
  const broken = answerRead.holed || answerRead.leaveFt < pending.offenseLeaveFt;
  if (broken) {
    finishSnap(state, { ...base, result: 'brokenUp', yards: 0, turnover: false, answer: answer(true) }, events);
    return;
  }
  finishSnap(state, { ...base, result: spec.kind === 'pass' ? 'complete' : 'gain', yards: pending.gainYards, turnover: false, answer: answer(false) }, events);
}

/** A gain that stood: the defense "has seen it" (Baseball's earned hit). */
function earned(outcome: Outcome): boolean {
  return !outcome.turnover && outcome.result !== 'brokenUp' && outcome.yards > 0;
}

/** Tighten a play; once every play has gained, all five reset (Baseball's earnHit). */
function earnPlay(state: FootballState, cup: FootballCup, events: FootballEvent[]): void {
  const team = state.offense;
  const stage = Math.min(3, state.stages[team][cup] + 1) as FootballStage;
  state.stages[team][cup] = stage;
  state.earned[team][cup] = true;
  events.push({ type: 'seen', team, cup, stage });
  if (FOOTBALL_CUPS.every((c) => state.earned[team][c])) {
    state.stages[team] = freshStages();
    state.earned[team] = freshEarned();
    events.push({ type: 'reset', team });
  }
}

function finishSnap(state: FootballState, outcome: Outcome, events: FootballEvent[]): void {
  const team = state.offense;
  const fromYl = state.yl;
  const goal = 100 - fromYl;
  const yards = Math.min(outcome.yards, goal);
  const toYl = fromYl + yards;
  const touchdown = !outcome.turnover && toYl >= 100;
  const firstDown = !outcome.turnover && !touchdown && !state.twoPoint && yards >= state.togo;
  const snap: FootballSnap = {
    team, cup: outcome.cup, result: outcome.result, yards: outcome.turnover ? 0 : yards, fromYl, toYl: outcome.turnover ? fromYl : toYl,
    down: state.down, togo: state.togo, offenseLeaveFt: outcome.offenseLeaveFt, offenseHoled: outcome.offenseHoled,
    answer: outcome.answer, stage: outcome.stage, firstDown, touchdown, turnover: outcome.turnover, twoPoint: state.twoPoint,
  };
  state.lastSnap = snap;
  state.snaps += 1;
  events.push({ type: 'snap', snap });

  if (state.twoPoint) {
    // One snap from the 2: 8 or 6. It neither uses the clock nor tightens a play.
    const made = !outcome.turnover && yards >= 2;
    state.twoPoint = false;
    if (made) state.scores[team] += 2;
    events.push({ type: 'try', team, points: made ? 2 : 0, twoPoint: true });
    endDrive(state, 'touchdown', FOOTBALL_START_YL, events);
    return;
  }

  state.drive.snaps += 1;
  if (!outcome.turnover) state.drive.yards += yards;
  if (!outcome.turnover && yards > state.longestPlay[team]) state.longestPlay[team] = yards;
  if (earned(outcome)) earnPlay(state, outcome.cup, events);

  if (outcome.turnover) {
    const how = outcome.result === 'fumble' ? 'fumble' : 'interception';
    if (how === 'fumble') state.fumblesLost[team] += 1;
    else state.interceptionsThrown[team] += 1;
    events.push({ type: 'turnover', team, how });
    // Spotted at the line of scrimmage; no return.
    endDrive(state, how, 100 - fromYl, events);
    return;
  }
  if (touchdown) {
    scoreTouchdown(state, events);
    return;
  }
  if (toYl <= 0) {
    const defense = footballDefense(state);
    state.scores[defense] += 2;
    events.push({ type: 'safety', team: defense });
    endDrive(state, 'safety', FOOTBALL_START_YL, events);
    return;
  }
  if (firstDown) {
    state.yl = toYl;
    state.down = 1;
    state.togo = Math.min(10, 100 - toYl);
    events.push({ type: 'firstDown', team, yl: toYl });
  } else if (state.down === 4) {
    events.push({ type: 'turnover', team, how: 'downs' });
    endDrive(state, 'downs', 100 - toYl, events);
    return;
  } else {
    state.yl = toYl;
    state.togo -= yards;
    state.down = (state.down + 1) as 1 | 2 | 3 | 4;
  }
  nextSnap(state, events);
}

/** After a snap that keeps the drive going: 4th down gets the call card. A drive always plays to its end. */
function nextSnap(state: FootballState, events: FootballEvent[]): void {
  // Behind on the last drive with only GO left: no card, the snap is on.
  if (state.down === 4 && footballFourthDownCalls(state).length === 1) {
    state.phase = 'offense';
    return;
  }
  if (state.down === 4) {
    state.phase = 'fourthDown';
    events.push({ type: 'fourthDown', team: state.offense });
  } else {
    state.phase = 'offense';
  }
}

function scoreTouchdown(state: FootballState, events: FootballEvent[]): void {
  const team = state.offense;
  state.scores[team] += 6;
  state.touchdowns[team] += 1;
  events.push({ type: 'touchdown', team });
  // A walk-off (Cole, 2026-11-01): on the game's last possession a touchdown
  // that ties or leads takes the 7 and ends the game. Keeping 7 already wins,
  // so the try could only cost the win; no card is shown. From the second
  // overtime round there is no 7: a lead ends it on the 6, a tie still goes for two.
  if (footballLastPossession(state)) {
    const margin = state.scores[team] - state.scores[footballDefense(state)];
    if (state.overtime >= 2 ? margin > 0 : margin >= 0) {
      if (state.overtime < 2) {
        state.scores[team] += 1;
        events.push({ type: 'try', team, points: 1, twoPoint: false });
      }
      endDrive(state, 'touchdown', FOOTBALL_START_YL, events);
      return;
    }
  }
  // From the second overtime round a touchdown must go for two.
  if (state.overtime >= 2) {
    startTwoPoint(state);
    return;
  }
  // Go for two is offered only when it can matter: each side's last drive and overtime.
  if (footballLateGame(state)) {
    state.phase = 'tryChoice';
    events.push({ type: 'tryChoice', team });
    return;
  }
  state.scores[team] += 1;
  events.push({ type: 'try', team, points: 1, twoPoint: false });
  endDrive(state, 'touchdown', FOOTBALL_START_YL, events);
}

function startTwoPoint(state: FootballState): void {
  state.twoPoint = true;
  state.yl = FOOTBALL_TWO_POINT_YL;
  state.down = 1;
  state.togo = 2;
  state.phase = 'offense';
}

function endDrive(state: FootballState, result: FootballDriveResult, nextYl: number, events: FootballEvent[]): void {
  const team = state.offense;
  state.drives.push({
    team,
    half: state.overtime > 0 ? 2 + state.overtime : state.half,
    startYl: state.drive.startYl,
    snaps: state.drive.snaps,
    yards: state.drive.yards,
    result,
    points: state.scores[team] - state.drive.offenseScoreAtStart,
  });
  state.pending = null;
  state.twoPoint = false;
  advancePossession(state, nextYl, events);
}

function startDrive(state: FootballState, team: FootballTeam, yl: number, events: FootballEvent[]): void {
  state.offense = team;
  state.yl = yl;
  state.down = 1;
  state.togo = Math.min(10, 100 - yl);
  state.pending = null;
  state.twoPoint = false;
  state.drive = { startYl: yl, snaps: 0, yards: 0, offenseScoreAtStart: state.scores[team] };
  events.push({ type: 'driveStart', team, yl, half: state.overtime > 0 ? 2 + state.overtime : state.half });
  state.phase = 'offense';
}

function finish(state: FootballState, events: FootballEvent[]): void {
  const winner: FootballTeam = state.scores[0] > state.scores[1] ? 0 : 1;
  state.winner = winner;
  state.phase = 'complete';
  state.pending = null;
  events.push({ type: 'gameOver', winner });
}

function advancePossession(state: FootballState, nextYl: number, events: FootballEvent[]): void {
  if (state.overtime === 0) {
    state.drivesLeft = Math.max(0, state.drivesLeft - 1);
    if (state.drivesLeft > 0) {
      startDrive(state, footballDefense(state), nextYl, events);
      return;
    }
    if (state.half === 1) {
      // Halftime: home receives the second half at the opponent's 35.
      state.half = 2;
      state.drivesLeft = state.halfDrives;
      events.push({ type: 'halftime' });
      startDrive(state, 1, FOOTBALL_START_YL, events);
      return;
    }
    if (state.scores[0] !== state.scores[1]) {
      finish(state, events);
      return;
    }
    state.overtime = 1;
    events.push({ type: 'overtime', round: 1 });
    startDrive(state, 0, FOOTBALL_OVERTIME_YL, events);
    return;
  }
  // Overtime: one possession each from the opponent's 25, away first.
  if (state.offense === 0) {
    startDrive(state, 1, FOOTBALL_OVERTIME_YL, events);
    return;
  }
  if (state.scores[0] !== state.scores[1]) {
    finish(state, events);
    return;
  }
  state.overtime += 1;
  events.push({ type: 'overtime', round: state.overtime });
  startDrive(state, 0, FOOTBALL_OVERTIME_YL, events);
}

function applyKick(state: FootballState, read: FootballRead, events: FootballEvent[]): boolean {
  const kickFt = footballKickFt(state);
  if (!read.kick || read.kick.targetFt !== kickFt) return false;
  const team = state.offense;
  const good = read.kick.holed || read.kick.leaveFt <= FOOTBALL_FG_RING_FT;
  state.drive.snaps += 1;
  if (good) {
    // In its ring (or holed): the defense gets one putt at the kick cup to
    // block it (Cole, 2026-10-18). A missed kick gets none.
    state.phase = 'block';
    events.push({ type: 'blockChance', team: footballDefense(state), kickYards: footballKickYards(state) });
    return true;
  }
  events.push({ type: 'fieldGoal', team, good: false, kickYards: footballKickYards(state) });
  // A miss: the other side takes over at the line of scrimmage (or its 20).
  endDrive(state, 'missedFieldGoal', Math.max(FOOTBALL_PUNT_TOUCHBACK_YL, 100 - state.yl), events);
  return true;
}

/** The defense's block attempt: holed blocks the kick (a miss, same spot rule); anything else and the kick is good. */
function applyBlock(state: FootballState, read: FootballRead, events: FootballEvent[]): boolean {
  const kickFt = footballKickFt(state);
  if (!read.kick || read.kick.targetFt !== kickFt) return false;
  const team = state.offense;
  const kickYards = footballKickYards(state);
  if (read.kick.holed) {
    events.push({ type: 'fieldGoal', team, good: false, kickYards, blocked: true });
    endDrive(state, 'blockedFieldGoal', Math.max(FOOTBALL_PUNT_TOUCHBACK_YL, 100 - state.yl), events);
    return true;
  }
  events.push({ type: 'fieldGoal', team, good: true, kickYards });
  state.scores[team] += 3;
  state.fieldGoals[team] += 1;
  endDrive(state, 'fieldGoal', FOOTBALL_START_YL, events);
  return true;
}

/**
 * One putt. The phase decides whose: the offense's snap, the defense's
 * answer, or the kick. A putt out of phase (a choice is pending, the game is
 * over) or a kick read against the wrong cup changes nothing.
 *
 * A gate strike does not count at all (Cole, 2026-10-16): the state comes
 * back unchanged and the same putter putts again.
 */
export function applyFootballPutt(previous: FootballState, read: FootballRead): AppliedFootball {
  if (read.gateHit) return { state: previous, events: [] };
  if (previous.phase !== 'offense' && previous.phase !== 'defense' && previous.phase !== 'kick' && previous.phase !== 'block') return { state: previous, events: [] };
  const state = clone(previous);
  const events: FootballEvent[] = [];
  if (state.phase === 'offense') applyOffense(state, read, events);
  else if (state.phase === 'defense') applyDefense(state, read, events);
  else if (state.phase === 'block') {
    if (!applyBlock(state, read, events)) return { state: previous, events: [] };
  } else if (!applyKick(state, read, events)) return { state: previous, events: [] };
  return { state, events };
}

// ---------------------------------------------------------------------------
// Clinched (Cole, 2026-10-18): the most one drive can score is 8 (a
// touchdown and two). When the trailing side trails by more than 8 times the
// drives it has left, the game is decided; the side that cannot catch up may
// end it as a normal result.
// ---------------------------------------------------------------------------

/** The most one drive can score: a touchdown and a two-point try. */
export const FOOTBALL_MAX_DRIVE_POINTS = 8;

/**
 * Each side's drives still to come in regulation, the one being played
 * counted for the side that has the ball. Away receives the first half and
 * home the second; drives alternate within a half.
 */
export function footballDrivesRemaining(state: Pick<FootballState, 'half' | 'drivesLeft' | 'halfDrives' | 'offense' | 'overtime' | 'phase'>): [number, number] {
  const left: [number, number] = [0, 0];
  if (state.phase === 'complete' || state.overtime > 0) return left;
  let team: FootballTeam = state.offense;
  for (let index = 0; index < state.drivesLeft; index += 1) {
    left[team] += 1;
    team = team === 0 ? 1 : 0;
  }
  if (state.half === 1) {
    team = 1;
    for (let index = 0; index < state.halfDrives; index += 1) {
      left[team] += 1;
      team = team === 0 ? 1 : 0;
    }
  }
  return left;
}

export interface FootballClinch {
  /** The side that has won it. */
  leader: FootballTeam;
  /** The side that cannot catch up: it decides whether to play on. */
  trailer: FootballTeam;
  /** Points the trailer needs to tie: the deficit. */
  need: number;
  /** The most the trailer can still score. */
  best: number;
}

/**
 * Whether the game is decided while the trailing side still has play of its
 * own to come: in regulation, the trailer's best (8 a drive, its own drives
 * only; the leader's drives can only help the leader) is less than the
 * deficit. Equal is not clinched: a tie goes to overtime. Null otherwise.
 *
 * Only offered while the trailer has a drive still to play (Cole,
 * 2026-10-19: "Shouldn't need to ask to play it out when you score on the
 * very last possession"): with none left the game ends by itself after
 * whatever is being played. A trailer in its try has spent that drive; the
 * try is played normally, and counts 2 toward its best.
 */
export function footballClinch(state: FootballState): FootballClinch | null {
  if (state.phase === 'complete' || state.overtime > 0) return null;
  const [away, home] = state.scores;
  if (away === home) return null;
  const leader: FootballTeam = away > home ? 0 : 1;
  const trailer: FootballTeam = leader === 0 ? 1 : 0;
  const inTry = state.offense === trailer && (state.phase === 'tryChoice' || state.twoPoint);
  const drives = footballDrivesRemaining(state)[trailer] - (inTry ? 1 : 0);
  if (drives <= 0) return null;
  const need = state.scores[leader] - state.scores[trailer];
  const best = FOOTBALL_MAX_DRIVE_POINTS * drives + (inTry ? 2 : 0);
  return need > best ? { leader, trailer, need, best } : null;
}

/** End a clinched game now: the leader wins, recorded as a normal result. Unchanged when it is not clinched. */
export function endFootballGameClinched(previous: FootballState): AppliedFootball {
  const clinch = footballClinch(previous);
  if (!clinch) return { state: previous, events: [] };
  const state = clone(previous);
  state.winner = clinch.leader;
  state.phase = 'complete';
  state.pending = null;
  state.twoPoint = false;
  const events: FootballEvent[] = [{ type: 'gameOver', winner: clinch.leader, clinched: true }];
  return { state, events };
}

/** 4th down: GO FOR IT, KICK (in range) or PUNT (not inside the opponent's 40). */
export function chooseFootballFourthDown(previous: FootballState, call: FootballFourthDownCall): AppliedFootball {
  if (previous.phase !== 'fourthDown' || !footballFourthDownCalls(previous).includes(call)) return { state: previous, events: [] };
  const state = clone(previous);
  const events: FootballEvent[] = [];
  if (call === 'go') {
    state.phase = 'offense';
  } else if (call === 'kick') {
    state.phase = 'kick';
  } else {
    // An automatic 40-yard punt; past the goal line is a touchback to the 20.
    const spot = state.yl + FOOTBALL_PUNT_NET_YARDS;
    const receivingYl = spot >= 100 ? FOOTBALL_PUNT_TOUCHBACK_YL : 100 - spot;
    events.push({ type: 'punt', team: state.offense, receivingYl });
    state.drive.snaps += 1;
    endDrive(state, 'punt', receivingYl, events);
  }
  return { state, events };
}

/** After a late touchdown or one in overtime round 1: take the 7, or go for two (8 or 6). */
export function chooseFootballTry(previous: FootballState, call: FootballTryCall): AppliedFootball {
  if (previous.phase !== 'tryChoice') return { state: previous, events: [] };
  const state = clone(previous);
  const events: FootballEvent[] = [];
  if (call === 'goForTwo') {
    startTwoPoint(state);
    return { state, events };
  }
  state.scores[state.offense] += 1;
  events.push({ type: 'try', team: state.offense, points: 1, twoPoint: false });
  endDrive(state, 'touchdown', FOOTBALL_START_YL, events);
  return { state, events };
}
